import React from "react";
import { motion, useInView } from "framer-motion";

const defaultVariants = {
  fadeUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0 },
  },
  scale: {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1 },
  },
};

const defaultTransition = {
  duration: 0.5,
  ease: [0.25, 0.46, 0.45, 0.94],
};

/**
 * Wraps children and animates them when they scroll into view.
 * @param {string} variant - 'fadeUp' | 'fadeIn' | 'fadeLeft' | 'fadeRight' | 'scale'
 * @param {number} delay - Stagger delay in seconds
 * @param {number} amount - Fraction of element in view to trigger (0-1)
 * @param {object} transition - Optional custom transition
 * @param {string} className - Optional class for the motion div
 * @param {boolean} once - If true, animate only once (default true)
 */
const ScrollReveal = ({
  children,
  variant = "fadeUp",
  delay = 0,
  amount = 0.15,
  transition,
  className = "",
  once = true,
  as = "div",
  ...rest
}) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { amount, once });
  const variants = defaultVariants[variant] || defaultVariants.fadeUp;
  const mergedTransition = { ...defaultTransition, delay, ...transition };

  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={mergedTransition}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

export default ScrollReveal;
export { defaultVariants, defaultTransition };
